/**
 * MatchResultCard — post-match recap card with eight bespoke theme layouts.
 *
 * Each theme is a complete visual reinterpretation of the same content —
 * not a colour swap. The same fields (home/away/scores/goals/meta) render as:
 *   VICTORY    — winner's side floods bright, loser fades to shadow
 *   STADIUM    — floodlit night photo + LED-scoreboard score
 *   NEWSPAPER  — cream paper + bold serif headline ("HOSTS STUN")
 *   HALF_HALF  — diagonal split filled with each team's colour
 *   NEON       — synthwave grid floor + cyan/pink glow
 *   MINIMAL    — off-white, ONE massive score, whisper team labels
 *   CINEMA     — pitch black + gold spotlight on the winner
 *   TICKER     — Sky Sports / ESPN broadcast lower-third bar
 *
 * Winner emphasis (auto-detected from score) only applies to VICTORY + CINEMA;
 * all other themes treat both teams neutrally.
 */
import { forwardRef } from "react";
import FbkCardShell from "./FbkCardShell";
import { getNationColors } from "./nationColors";
import {
  defaultFooterLeft,
  resolveMatchWinner,
  type CardFormat,
  type MatchResultCardContent,
  type MatchResultGoal,
  type MatchResultTeam,
  type MatchResultTheme,
} from "./types";

interface MatchResultCardProps {
  format: CardFormat;
  content: MatchResultCardContent;
}

/** flagcdn fallback when the team doesn't carry an explicit flag URL. */
function flagSrc(team: MatchResultTeam): string | undefined {
  if (team.flagUrl) return team.flagUrl;
  if (team.code) return `https://flagcdn.com/w320/${team.code.toLowerCase()}.png`;
  return undefined;
}

/** Hex helper: brand colour resolution per team (explicit override > nation table > brand fallback). */
function teamColor(team: MatchResultTeam, fallback: string): string {
  if (team.color) return team.color;
  const colors = team.code ? getNationColors(team.code) : null;
  if (colors?.primary) return colors.primary;
  return fallback;
}

/** Luminance check — used to pick readable text colour on top of a team's flood colour. */
function luminance(hex: string): number {
  const c = hex.replace("#", "").trim();
  if (c.length !== 6) return 0;
  const r = parseInt(c.slice(0, 2), 16) / 255;
  const g = parseInt(c.slice(2, 4), 16) / 255;
  const b = parseInt(c.slice(4, 6), 16) / 255;
  return 0.2126 * r + 0.7152 * g + 0.0722 * b;
}

// ─── Shared font stacks ──────────────────────────────────────────────────────
const FONT_BEBAS = "'Bebas Neue', 'Anton', sans-serif";
const FONT_INTER = "'Inter', system-ui, sans-serif";
const FONT_PLAYFAIR = "'Playfair Display', Georgia, serif";
const FONT_MONO = "ui-monospace, 'JetBrains Mono', monospace";

// ─── Card root ───────────────────────────────────────────────────────────────

const MatchResultCard = forwardRef<HTMLDivElement, MatchResultCardProps>(
  function MatchResultCard({ format, content }, ref) {
    const winner = resolveMatchWinner(content.homeScore, content.awayScore);
    const themeId = content.theme;

    return (
      <FbkCardShell
        ref={ref}
        format={format}
        footerLeft={content.footerLeft || defaultFooterLeft("MATCH_RESULT")}
        footerRight={content.hashtags || "#WC2026 · #FBK"}
        header={content.header}
        background={content.style?.background}
        customBaseGradient={pickBaseGradient(themeId, content)}
        headerOnLight={themeUsesLightHeader(themeId)}
        suppressBrandOverlays={themeSuppressesBrand(themeId)}
        hideFooter={content.footer?.hide}
      >
        {renderTheme(themeId, content, winner, format)}
      </FbkCardShell>
    );
  },
);

export default MatchResultCard;

// ─── Theme dispatcher ────────────────────────────────────────────────────────

function renderTheme(
  themeId: MatchResultTheme,
  content: MatchResultCardContent,
  winner: "home" | "away" | "draw",
  format: CardFormat,
) {
  switch (themeId) {
    case "victory":
      return <VictoryTheme content={content} winner={winner} format={format} />;
    case "stadium":
      return <StadiumTheme content={content} format={format} />;
    case "newspaper":
      return <NewspaperTheme content={content} format={format} />;
    case "half_half":
      return <HalfHalfTheme content={content} format={format} />;
    case "neon":
      return <NeonTheme content={content} format={format} />;
    case "minimal":
      return <MinimalTheme content={content} format={format} />;
    case "cinema":
      return <CinemaTheme content={content} winner={winner} format={format} />;
    case "ticker":
      return <TickerTheme content={content} format={format} />;
  }
}

function pickBaseGradient(
  themeId: MatchResultTheme,
  _content: MatchResultCardContent,
): string | undefined {
  switch (themeId) {
    case "victory":
      // Background is a hard split so the winner's colour floods one side.
      return undefined; // handled inside VictoryTheme
    case "stadium":
      return "radial-gradient(ellipse 80% 60% at 50% 30%, #1a2e35 0%, #07121a 60%, #000000 100%)";
    case "newspaper":
      return "#F4EDD9";
    case "half_half":
      return undefined; // handled inside HalfHalfTheme
    case "neon":
      return "linear-gradient(180deg, #0a0118 0%, #1a0530 40%, #2d0a4a 70%, #160526 100%)";
    case "minimal":
      return "#FAFAFA";
    case "cinema":
      return "radial-gradient(ellipse 65% 50% at 50% 45%, #1a1a1a 0%, #0a0a0a 50%, #000000 100%)";
    case "ticker":
      return "linear-gradient(180deg, #0D8C3C 0%, #0A4724 100%)";
  }
}

function themeUsesLightHeader(themeId: MatchResultTheme): boolean {
  return themeId === "newspaper" || themeId === "minimal";
}

function themeSuppressesBrand(themeId: MatchResultTheme): boolean {
  // Themes with their own custom background language don't want the gold-halo + grid overlays.
  return (
    themeId === "stadium" ||
    themeId === "newspaper" ||
    themeId === "half_half" ||
    themeId === "neon" ||
    themeId === "minimal" ||
    themeId === "cinema" ||
    themeId === "victory"
  );
}

// ─── Shared bits ─────────────────────────────────────────────────────────────

function FlagImg({
  team,
  width,
  rounded = 12,
  ring,
}: {
  team: MatchResultTeam;
  width: number;
  rounded?: number;
  ring?: string;
}) {
  const src = flagSrc(team);
  if (!src) return null;
  return (
    <img
      src={src}
      alt=""
      crossOrigin={src.startsWith("data:") ? undefined : "anonymous"}
      style={{
        width,
        height: width * 0.66,
        objectFit: "cover",
        borderRadius: rounded,
        boxShadow: ring ? `0 0 0 3px ${ring}, 0 12px 24px rgba(0,0,0,0.35)` : "0 12px 24px rgba(0,0,0,0.35)",
        background: "rgba(255,255,255,0.04)",
      }}
    />
  );
}

function GoalRow({
  goal,
  color,
  fontSize,
}: {
  goal: MatchResultGoal;
  color: string;
  fontSize: number;
}) {
  return (
    <div
      style={{
        display: "flex",
        alignItems: "baseline",
        gap: 10,
        fontFamily: FONT_INTER,
        fontSize,
        color,
        fontWeight: 700,
      }}
    >
      <span style={{ fontSize: fontSize * 0.7, opacity: 0.6 }}>⚽</span>
      <span>{goal.scorer}</span>
      <span style={{ opacity: 0.6, fontFamily: FONT_MONO, fontSize: fontSize * 0.85 }}>
        {goal.minute}
      </span>
    </div>
  );
}

// ─── THEME 1 · VICTORY ──────────────────────────────────────────────────────
//   Winner's side floods their team colour. Loser side fades to near-black with
//   a faded grey. Score sits dead-centre over the split; winner's score in white,
//   loser's score in low-opacity grey. Draws split 50/50 in both team colours.

function VictoryTheme({
  content,
  winner,
  format,
}: {
  content: MatchResultCardContent;
  winner: "home" | "away" | "draw";
  format: CardFormat;
}) {
  const isStory = format === "story";
  const homeColor = teamColor(content.home, "#0D8C3C");
  const awayColor = teamColor(content.away, "#0D5C2E");

  const homeIsWinner = winner === "home";
  const awayIsWinner = winner === "away";

  // Backgrounds per side
  const leftBg = homeIsWinner
    ? `linear-gradient(135deg, ${homeColor} 0%, ${homeColor}cc 100%)`
    : winner === "draw"
      ? `linear-gradient(135deg, ${homeColor}ee 0%, ${homeColor}99 100%)`
      : `linear-gradient(135deg, #1a1a1a 0%, #0a0a0a 100%)`;
  const rightBg = awayIsWinner
    ? `linear-gradient(135deg, ${awayColor} 0%, ${awayColor}cc 100%)`
    : winner === "draw"
      ? `linear-gradient(135deg, ${awayColor}ee 0%, ${awayColor}99 100%)`
      : `linear-gradient(135deg, #1a1a1a 0%, #0a0a0a 100%)`;

  const homeTextColor = homeIsWinner || winner === "draw"
    ? luminance(homeColor) > 0.55 ? "#0a0a0a" : "#FFFFFF"
    : "rgba(255,255,255,0.35)";
  const awayTextColor = awayIsWinner || winner === "draw"
    ? luminance(awayColor) > 0.55 ? "#0a0a0a" : "#FFFFFF"
    : "rgba(255,255,255,0.35)";

  return (
    <div
      style={{
        position: "absolute",
        inset: 0,
        display: "flex",
        flexDirection: "column",
        zIndex: 2,
      }}
    >
      {/* Two-zone background overlay */}
      <div style={{ position: "absolute", inset: 0, display: "flex", zIndex: -1 }}>
        <div style={{ flex: 1, background: leftBg }} />
        <div style={{ flex: 1, background: rightBg }} />
      </div>

      {/* Eyebrow */}
      <div
        style={{
          textAlign: "center",
          fontFamily: FONT_INTER,
          fontSize: isStory ? 26 : 22,
          fontWeight: 800,
          color: "#FFD700",
          letterSpacing: "0.4em",
          textTransform: "uppercase",
          marginTop: isStory ? 60 : 32,
        }}
      >
        {content.eyebrow}
      </div>

      {/* Main row — flag, score, flag */}
      <div
        style={{
          flex: 1,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          gap: isStory ? 24 : 18,
          padding: isStory ? "40px 32px" : "24px 22px",
        }}
      >
        <SideBlock
          team={content.home}
          score={content.homeScore}
          isWinner={homeIsWinner}
          isDraw={winner === "draw"}
          textColor={homeTextColor}
          isStory={isStory}
          align="left"
        />
        <div
          style={{
            fontFamily: FONT_BEBAS,
            fontSize: isStory ? 220 : 168,
            color: "rgba(255,255,255,0.55)",
            lineHeight: 1,
            padding: "0 16px",
          }}
        >
          −
        </div>
        <SideBlock
          team={content.away}
          score={content.awayScore}
          isWinner={awayIsWinner}
          isDraw={winner === "draw"}
          textColor={awayTextColor}
          isStory={isStory}
          align="right"
        />
      </div>

      {/* Footer info */}
      <div
        style={{
          padding: isStory ? "32px 40px" : "20px 28px",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          gap: 12,
        }}
      >
        {content.headline && (
          <div
            style={{
              fontFamily: FONT_BEBAS,
              fontSize: isStory ? 56 : 44,
              color: "#FFFFFF",
              letterSpacing: "0.04em",
              textTransform: "uppercase",
              textShadow: "0 4px 12px rgba(0,0,0,0.6)",
              textAlign: "center",
            }}
          >
            {content.headline}
          </div>
        )}
        {content.tagline && (
          <div
            style={{
              fontFamily: FONT_INTER,
              fontSize: isStory ? 26 : 22,
              fontStyle: "italic",
              color: "rgba(255,255,255,0.92)",
              textAlign: "center",
              maxWidth: isStory ? 780 : 620,
              textShadow: "0 2px 6px rgba(0,0,0,0.5)",
            }}
          >
            "{content.tagline}"
          </div>
        )}
        <MetaStrip content={content} isStory={isStory} color="rgba(255,255,255,0.85)" />
      </div>
    </div>
  );
}

function SideBlock({
  team,
  score,
  isWinner,
  isDraw,
  textColor,
  isStory,
  align,
}: {
  team: MatchResultTeam;
  score: number;
  isWinner: boolean;
  isDraw: boolean;
  textColor: string;
  isStory: boolean;
  align: "left" | "right";
}) {
  return (
    <div
      style={{
        flex: 1,
        display: "flex",
        flexDirection: "column",
        alignItems: align === "left" ? "flex-end" : "flex-start",
        gap: isStory ? 18 : 12,
        minWidth: 0,
      }}
    >
      <FlagImg
        team={team}
        width={isStory ? 200 : 156}
        ring={isWinner ? "#FFD700" : isDraw ? "rgba(255,255,255,0.5)" : "rgba(255,255,255,0.15)"}
      />
      <div
        style={{
          fontFamily: FONT_BEBAS,
          fontSize: isStory ? 64 : 50,
          color: textColor,
          letterSpacing: "0.03em",
          textTransform: "uppercase",
          lineHeight: 1,
          textAlign: align,
        }}
      >
        {team.name}
      </div>
      <div
        style={{
          fontFamily: FONT_BEBAS,
          fontSize: isStory ? 320 : 240,
          color: textColor,
          lineHeight: 0.85,
          letterSpacing: "-0.05em",
          textShadow: isWinner
            ? "0 0 80px rgba(255,215,0,0.45), 0 6px 24px rgba(0,0,0,0.5)"
            : "0 4px 12px rgba(0,0,0,0.45)",
          fontVariantNumeric: "tabular-nums",
        }}
      >
        {score}
      </div>
    </div>
  );
}

function MetaStrip({
  content,
  isStory,
  color,
}: {
  content: MatchResultCardContent;
  isStory: boolean;
  color: string;
}) {
  const bits = [content.dateLabel, content.venue, content.aggregateNote].filter(
    (s): s is string => !!s,
  );
  if (bits.length === 0) return null;
  return (
    <div
      style={{
        fontFamily: FONT_MONO,
        fontSize: isStory ? 18 : 15,
        color,
        letterSpacing: "0.3em",
        textTransform: "uppercase",
        textAlign: "center",
        marginTop: 6,
      }}
    >
      {bits.join(" · ")}
    </div>
  );
}

// ─── THEME 2 · STADIUM ──────────────────────────────────────────────────────
//   Floodlit night stadium feel. SVG floodlight cones + crowd-suggestion bands.
//   Score in giant LED-scoreboard treatment with neon glow.

function StadiumTheme({
  content,
  format,
}: {
  content: MatchResultCardContent;
  format: CardFormat;
}) {
  const isStory = format === "story";
  return (
    <>
      {/* Floodlight cones */}
      <svg
        viewBox="0 0 100 100"
        preserveAspectRatio="none"
        style={{ position: "absolute", inset: 0, width: "100%", height: "100%", zIndex: 1, opacity: 0.35 }}
      >
        <defs>
          <linearGradient id="cone1" x1="50%" y1="0%" x2="50%" y2="100%">
            <stop offset="0%" stopColor="#fff8dc" stopOpacity="0.5" />
            <stop offset="100%" stopColor="#fff8dc" stopOpacity="0" />
          </linearGradient>
        </defs>
        <polygon points="20,0 0,40 40,40" fill="url(#cone1)" />
        <polygon points="80,0 60,40 100,40" fill="url(#cone1)" />
        <polygon points="50,0 35,30 65,30" fill="url(#cone1)" />
      </svg>
      {/* Crowd suggestion stripes */}
      <div
        style={{
          position: "absolute",
          inset: 0,
          background:
            "repeating-linear-gradient(180deg, rgba(255,255,255,0.02) 0px, rgba(255,255,255,0.02) 2px, transparent 2px, transparent 8px)",
          opacity: 0.4,
          zIndex: 1,
        }}
      />
      <div
        style={{
          position: "relative",
          zIndex: 2,
          flex: 1,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          padding: isStory ? 48 : 32,
        }}
      >
        <div
          style={{
            fontFamily: FONT_MONO,
            fontSize: isStory ? 26 : 22,
            color: "#FFD700",
            letterSpacing: "0.5em",
            textTransform: "uppercase",
            fontWeight: 800,
            marginBottom: isStory ? 36 : 24,
          }}
        >
          {content.eyebrow}
        </div>

        {/* LED-style scoreboard */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: isStory ? 28 : 20,
            padding: isStory ? "32px 48px" : "22px 32px",
            background: "rgba(0,0,0,0.55)",
            border: "3px solid rgba(255,215,0,0.45)",
            borderRadius: 24,
            boxShadow: "0 0 80px rgba(255,215,0,0.25), inset 0 0 40px rgba(0,0,0,0.5)",
          }}
        >
          <ScoreboardSide team={content.home} score={content.homeScore} isStory={isStory} align="right" />
          <div
            style={{
              fontFamily: FONT_BEBAS,
              fontSize: isStory ? 180 : 134,
              color: "rgba(255,255,255,0.4)",
              lineHeight: 1,
            }}
          >
            :
          </div>
          <ScoreboardSide team={content.away} score={content.awayScore} isStory={isStory} align="left" />
        </div>

        {content.headline && (
          <div
            style={{
              marginTop: isStory ? 36 : 24,
              fontFamily: FONT_BEBAS,
              fontSize: isStory ? 64 : 48,
              color: "#FFFFFF",
              letterSpacing: "0.04em",
              textTransform: "uppercase",
              textAlign: "center",
              textShadow: "0 6px 20px rgba(0,0,0,0.6), 0 0 40px rgba(255,215,0,0.25)",
              maxWidth: isStory ? 880 : 720,
            }}
          >
            {content.headline}
          </div>
        )}
        {content.tagline && (
          <div
            style={{
              marginTop: isStory ? 18 : 12,
              fontFamily: FONT_INTER,
              fontSize: isStory ? 26 : 22,
              color: "rgba(255,255,255,0.88)",
              fontStyle: "italic",
              textAlign: "center",
              maxWidth: isStory ? 820 : 680,
            }}
          >
            "{content.tagline}"
          </div>
        )}

        {(content.goals?.length ?? 0) > 0 && (
          <GoalsStrip goals={content.goals!} isStory={isStory} textColor="rgba(255,255,255,0.92)" />
        )}

        <MetaStrip content={content} isStory={isStory} color="rgba(255,215,0,0.75)" />
      </div>
    </>
  );
}

function ScoreboardSide({
  team,
  score,
  isStory,
  align,
}: {
  team: MatchResultTeam;
  score: number;
  isStory: boolean;
  align: "left" | "right";
}) {
  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        alignItems: align === "left" ? "flex-start" : "flex-end",
        gap: isStory ? 12 : 8,
      }}
    >
      <FlagImg team={team} width={isStory ? 120 : 92} ring="rgba(255,215,0,0.5)" />
      <div
        style={{
          fontFamily: FONT_MONO,
          fontSize: isStory ? 22 : 18,
          color: "rgba(255,215,0,0.85)",
          letterSpacing: "0.32em",
          textTransform: "uppercase",
          fontWeight: 800,
        }}
      >
        {team.code.toUpperCase()}
      </div>
      <div
        style={{
          fontFamily: FONT_BEBAS,
          fontSize: isStory ? 240 : 180,
          color: "#FFD700",
          lineHeight: 0.9,
          letterSpacing: "-0.04em",
          textShadow: "0 0 28px rgba(255,215,0,0.7), 0 0 60px rgba(255,215,0,0.35)",
          fontVariantNumeric: "tabular-nums",
        }}
      >
        {score}
      </div>
    </div>
  );
}

// ─── THEME 3 · NEWSPAPER ────────────────────────────────────────────────────
//   Editorial morning paper. Cream BG, bold serif headline ("HOSTS STUN"),
//   the score is a small monospace stat below. Decorative rules. Date stamp.

function NewspaperTheme({
  content,
  format,
}: {
  content: MatchResultCardContent;
  format: CardFormat;
}) {
  const isStory = format === "story";
  const INK = "#1A2E1F";
  const ACCENT = "#A02928";
  const display = content.headline ||
    `${content.home.name.toUpperCase()} ${content.homeScore}–${content.awayScore} ${content.away.name.toUpperCase()}`;

  return (
    <div
      style={{
        position: "relative",
        zIndex: 2,
        flex: 1,
        display: "flex",
        flexDirection: "column",
        padding: isStory ? "80px 64px" : "48px 48px",
        color: INK,
      }}
    >
      {/* Top rule + edition strip */}
      <div
        style={{
          display: "flex",
          alignItems: "baseline",
          justifyContent: "space-between",
          paddingBottom: 12,
          borderBottom: `4px double ${INK}`,
          marginBottom: isStory ? 28 : 18,
        }}
      >
        <div
          style={{
            fontFamily: FONT_MONO,
            fontSize: isStory ? 18 : 15,
            letterSpacing: "0.3em",
            textTransform: "uppercase",
            fontWeight: 800,
          }}
        >
          FBK DAILY · MATCH REPORT
        </div>
        <div
          style={{
            fontFamily: FONT_MONO,
            fontSize: isStory ? 18 : 15,
            letterSpacing: "0.25em",
            textTransform: "uppercase",
            color: ACCENT,
            fontWeight: 800,
          }}
        >
          {content.eyebrow}
        </div>
      </div>

      {/* The mega headline */}
      <div
        style={{
          fontFamily: FONT_PLAYFAIR,
          fontSize: isStory ? 160 : 124,
          fontWeight: 800,
          fontStyle: "italic",
          lineHeight: 0.95,
          letterSpacing: "-0.02em",
          marginTop: isStory ? 32 : 18,
          marginBottom: isStory ? 32 : 22,
        }}
      >
        {display}.
      </div>

      {content.tagline && (
        <div
          style={{
            fontFamily: FONT_PLAYFAIR,
            fontSize: isStory ? 36 : 28,
            fontStyle: "italic",
            color: `${INK}cc`,
            lineHeight: 1.4,
            paddingLeft: 22,
            borderLeft: `4px solid ${ACCENT}`,
            marginBottom: isStory ? 32 : 22,
          }}
        >
          "{content.tagline}"
        </div>
      )}

      {/* Score bar — newspaper stat block */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          gap: isStory ? 36 : 24,
          padding: isStory ? "28px 32px" : "20px 22px",
          background: `${INK}0d`,
          border: `2px solid ${INK}`,
          marginTop: isStory ? 16 : 8,
        }}
      >
        <NewspaperTeamLabel team={content.home} score={content.homeScore} isStory={isStory} ink={INK} />
        <div
          style={{
            fontFamily: FONT_PLAYFAIR,
            fontSize: isStory ? 64 : 48,
            color: ACCENT,
            fontStyle: "italic",
            fontWeight: 800,
          }}
        >
          vs
        </div>
        <NewspaperTeamLabel team={content.away} score={content.awayScore} isStory={isStory} ink={INK} />
      </div>

      {/* Optional goal list as inline byline */}
      {(content.goals?.length ?? 0) > 0 && (
        <div
          style={{
            marginTop: isStory ? 28 : 18,
            fontFamily: FONT_INTER,
            fontSize: isStory ? 22 : 18,
            color: INK,
            lineHeight: 1.6,
            borderTop: `1px solid ${INK}33`,
            paddingTop: 14,
          }}
        >
          <span style={{ fontWeight: 800, marginRight: 8, color: ACCENT }}>SCORERS</span>
          {content.goals!.map((g, i) => (
            <span key={i} style={{ marginRight: 14 }}>
              {g.scorer} <span style={{ fontFamily: FONT_MONO, opacity: 0.7 }}>{g.minute}</span>
              {i < content.goals!.length - 1 && " ·"}
            </span>
          ))}
        </div>
      )}

      {content.motm && (
        <div
          style={{
            marginTop: 14,
            fontFamily: FONT_INTER,
            fontSize: isStory ? 22 : 18,
            color: INK,
          }}
        >
          <span style={{ fontWeight: 800, color: ACCENT, marginRight: 8 }}>MOTM</span>
          {content.motm}
        </div>
      )}

      <div style={{ flex: 1 }} />

      {/* Bottom byline */}
      <div
        style={{
          borderTop: `2px solid ${INK}`,
          paddingTop: 12,
          display: "flex",
          justifyContent: "space-between",
          fontFamily: FONT_MONO,
          fontSize: isStory ? 16 : 13,
          letterSpacing: "0.2em",
          textTransform: "uppercase",
          color: `${INK}b3`,
        }}
      >
        <span>{content.dateLabel || ""}</span>
        <span>{content.venue || ""}</span>
        <span>FBK · WC2026</span>
      </div>
    </div>
  );
}

function NewspaperTeamLabel({
  team,
  score,
  isStory,
  ink,
}: {
  team: MatchResultTeam;
  score: number;
  isStory: boolean;
  ink: string;
}) {
  return (
    <div style={{ display: "flex", alignItems: "center", gap: isStory ? 16 : 12 }}>
      <FlagImg team={team} width={isStory ? 80 : 60} rounded={6} />
      <div
        style={{
          fontFamily: FONT_PLAYFAIR,
          fontSize: isStory ? 56 : 42,
          fontWeight: 800,
          color: ink,
          letterSpacing: "-0.01em",
        }}
      >
        {team.name} <span style={{ color: "#A02928" }}>{score}</span>
      </div>
    </div>
  );
}

// ─── THEME 4 · HALF AND HALF ────────────────────────────────────────────────
//   Diagonal split filled with each team's primary colour, score on the seam.
//   Most universal — works for any matchup.

function HalfHalfTheme({
  content,
  format,
}: {
  content: MatchResultCardContent;
  format: CardFormat;
}) {
  const isStory = format === "story";
  const homeColor = teamColor(content.home, "#0D8C3C");
  const awayColor = teamColor(content.away, "#0D5C2E");

  const homeText = luminance(homeColor) > 0.55 ? "#0a0a0a" : "#FFFFFF";
  const awayText = luminance(awayColor) > 0.55 ? "#0a0a0a" : "#FFFFFF";

  return (
    <>
      {/* Diagonal split background */}
      <div
        style={{
          position: "absolute",
          inset: 0,
          background: `linear-gradient(115deg, ${homeColor} 0%, ${homeColor} 48%, #FFD700 49%, #FFD700 51%, ${awayColor} 52%, ${awayColor} 100%)`,
          zIndex: 1,
        }}
      />
      <div
        style={{
          position: "relative",
          zIndex: 2,
          flex: 1,
          display: "flex",
          flexDirection: "column",
        }}
      >
        <div
          style={{
            textAlign: "center",
            fontFamily: FONT_INTER,
            fontSize: isStory ? 26 : 22,
            fontWeight: 800,
            color: "#FFD700",
            letterSpacing: "0.4em",
            textTransform: "uppercase",
            marginTop: isStory ? 60 : 32,
            textShadow: "0 4px 16px rgba(0,0,0,0.5)",
          }}
        >
          {content.eyebrow}
        </div>

        <div
          style={{
            flex: 1,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: isStory ? "20px 32px" : "16px 22px",
          }}
        >
          {/* Home side */}
          <div
            style={{
              flex: 1,
              display: "flex",
              flexDirection: "column",
              alignItems: "flex-end",
              gap: isStory ? 22 : 16,
              padding: isStory ? "0 36px 0 0" : "0 24px 0 0",
            }}
          >
            <FlagImg team={content.home} width={isStory ? 220 : 168} ring="rgba(255,255,255,0.5)" />
            <div
              style={{
                fontFamily: FONT_BEBAS,
                fontSize: isStory ? 72 : 56,
                color: homeText,
                letterSpacing: "0.03em",
                textTransform: "uppercase",
                lineHeight: 1,
                textAlign: "right",
                textShadow: "0 4px 12px rgba(0,0,0,0.3)",
              }}
            >
              {content.home.name}
            </div>
            <div
              style={{
                fontFamily: FONT_BEBAS,
                fontSize: isStory ? 360 : 270,
                color: homeText,
                lineHeight: 0.85,
                letterSpacing: "-0.05em",
                textShadow: "0 8px 28px rgba(0,0,0,0.4)",
                fontVariantNumeric: "tabular-nums",
              }}
            >
              {content.homeScore}
            </div>
          </div>

          {/* Away side */}
          <div
            style={{
              flex: 1,
              display: "flex",
              flexDirection: "column",
              alignItems: "flex-start",
              gap: isStory ? 22 : 16,
              padding: isStory ? "0 0 0 36px" : "0 0 0 24px",
            }}
          >
            <FlagImg team={content.away} width={isStory ? 220 : 168} ring="rgba(255,255,255,0.5)" />
            <div
              style={{
                fontFamily: FONT_BEBAS,
                fontSize: isStory ? 72 : 56,
                color: awayText,
                letterSpacing: "0.03em",
                textTransform: "uppercase",
                lineHeight: 1,
                textAlign: "left",
                textShadow: "0 4px 12px rgba(0,0,0,0.3)",
              }}
            >
              {content.away.name}
            </div>
            <div
              style={{
                fontFamily: FONT_BEBAS,
                fontSize: isStory ? 360 : 270,
                color: awayText,
                lineHeight: 0.85,
                letterSpacing: "-0.05em",
                textShadow: "0 8px 28px rgba(0,0,0,0.4)",
                fontVariantNumeric: "tabular-nums",
              }}
            >
              {content.awayScore}
            </div>
          </div>
        </div>

        {/* Bottom info bar */}
        <div
          style={{
            padding: isStory ? "32px 40px" : "20px 28px",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: 10,
          }}
        >
          {content.tagline && (
            <div
              style={{
                fontFamily: FONT_INTER,
                fontSize: isStory ? 26 : 22,
                color: "rgba(255,255,255,0.95)",
                fontStyle: "italic",
                textAlign: "center",
                maxWidth: isStory ? 800 : 640,
                textShadow: "0 4px 12px rgba(0,0,0,0.5)",
              }}
            >
              "{content.tagline}"
            </div>
          )}
          <MetaStrip content={content} isStory={isStory} color="rgba(255,255,255,0.88)" />
        </div>
      </div>
    </>
  );
}

// ─── THEME 5 · NEON ─────────────────────────────────────────────────────────
//   Synthwave grid floor + cyan/pink neon score. Dark gradient BG.

function NeonTheme({
  content,
  format,
}: {
  content: MatchResultCardContent;
  format: CardFormat;
}) {
  const isStory = format === "story";
  return (
    <>
      {/* Grid floor */}
      <svg
        viewBox="0 0 100 100"
        preserveAspectRatio="none"
        style={{ position: "absolute", inset: 0, width: "100%", height: "100%", zIndex: 1, opacity: 0.45 }}
      >
        <defs>
          <linearGradient id="gridFade" x1="0%" y1="40%" x2="0%" y2="100%">
            <stop offset="0%" stopColor="#00f5ff" stopOpacity="0" />
            <stop offset="60%" stopColor="#ff00aa" stopOpacity="0.7" />
            <stop offset="100%" stopColor="#ff00aa" stopOpacity="0.3" />
          </linearGradient>
        </defs>
        {Array.from({ length: 14 }).map((_, i) => {
          const y = 50 + i * 4;
          return (
            <line
              key={`h${i}`}
              x1="0"
              y1={y}
              x2="100"
              y2={y}
              stroke="url(#gridFade)"
              strokeWidth="0.15"
              vectorEffect="non-scaling-stroke"
            />
          );
        })}
        {Array.from({ length: 21 }).map((_, i) => {
          const x = i * 5;
          return (
            <line
              key={`v${i}`}
              x1={x}
              y1="50"
              x2={50 + (x - 50) * 1.6}
              y2="100"
              stroke="url(#gridFade)"
              strokeWidth="0.15"
              vectorEffect="non-scaling-stroke"
            />
          );
        })}
        {/* Sun */}
        <circle cx="50" cy="50" r="22" fill="#ff00aa" opacity="0.18" />
        <circle cx="50" cy="50" r="14" fill="#ff00aa" opacity="0.25" />
      </svg>
      <div
        style={{
          position: "relative",
          zIndex: 2,
          flex: 1,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          padding: isStory ? "60px 40px" : "32px 28px",
        }}
      >
        <div
          style={{
            fontFamily: FONT_MONO,
            fontSize: isStory ? 26 : 22,
            color: "#00f5ff",
            letterSpacing: "0.4em",
            textTransform: "uppercase",
            fontWeight: 800,
            textShadow: "0 0 18px #00f5ff, 0 0 36px #00f5ff80",
            marginBottom: isStory ? 32 : 22,
          }}
        >
          {content.eyebrow}
        </div>

        {/* Score row */}
        <div
          style={{
            flex: 1,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: isStory ? 36 : 26,
          }}
        >
          <NeonScoreSide team={content.home} score={content.homeScore} isStory={isStory} color="#00f5ff" />
          <div
            style={{
              fontFamily: FONT_BEBAS,
              fontSize: isStory ? 220 : 168,
              color: "#FFFFFF",
              lineHeight: 1,
              textShadow:
                "0 0 24px #ffffff, 0 0 60px #ff00aa, 0 0 90px #00f5ff",
            }}
          >
            −
          </div>
          <NeonScoreSide team={content.away} score={content.awayScore} isStory={isStory} color="#ff00aa" />
        </div>

        {content.tagline && (
          <div
            style={{
              marginTop: isStory ? 28 : 18,
              fontFamily: FONT_INTER,
              fontSize: isStory ? 26 : 22,
              color: "#FFFFFF",
              fontStyle: "italic",
              textAlign: "center",
              maxWidth: isStory ? 820 : 660,
              textShadow: "0 0 18px rgba(0,245,255,0.5)",
            }}
          >
            "{content.tagline}"
          </div>
        )}
        <MetaStrip content={content} isStory={isStory} color="#00f5ff" />
      </div>
    </>
  );
}

function NeonScoreSide({
  team,
  score,
  isStory,
  color,
}: {
  team: MatchResultTeam;
  score: number;
  isStory: boolean;
  color: string;
}) {
  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        gap: isStory ? 16 : 12,
      }}
    >
      <FlagImg team={team} width={isStory ? 160 : 120} ring={`${color}80`} />
      <div
        style={{
          fontFamily: FONT_MONO,
          fontSize: isStory ? 24 : 19,
          color,
          letterSpacing: "0.35em",
          textTransform: "uppercase",
          fontWeight: 800,
          textShadow: `0 0 16px ${color}, 0 0 36px ${color}99`,
        }}
      >
        {team.code.toUpperCase()}
      </div>
      <div
        style={{
          fontFamily: FONT_BEBAS,
          fontSize: isStory ? 280 : 210,
          color: "#FFFFFF",
          lineHeight: 0.9,
          letterSpacing: "-0.04em",
          textShadow: `0 0 28px ${color}, 0 0 60px ${color}99, 0 0 100px ${color}66`,
          fontVariantNumeric: "tabular-nums",
        }}
      >
        {score}
      </div>
    </div>
  );
}

// ─── THEME 6 · MINIMAL ──────────────────────────────────────────────────────
//   Apple/Stripe refined. Off-white, ONE massive black score, whisper labels.

function MinimalTheme({
  content,
  format,
}: {
  content: MatchResultCardContent;
  format: CardFormat;
}) {
  const isStory = format === "story";
  const INK = "#0a0a0a";
  return (
    <div
      style={{
        position: "relative",
        zIndex: 2,
        flex: 1,
        display: "flex",
        flexDirection: "column",
        padding: isStory ? "80px 64px" : "56px 48px",
        color: INK,
      }}
    >
      <div
        style={{
          fontFamily: FONT_INTER,
          fontSize: isStory ? 18 : 15,
          fontWeight: 600,
          letterSpacing: "0.3em",
          textTransform: "uppercase",
          color: `${INK}88`,
          textAlign: "center",
        }}
      >
        {content.eyebrow}
      </div>

      <div style={{ flex: 1, display: "flex", flexDirection: "column", justifyContent: "center" }}>
        {/* Team codes — whisper labels */}
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            gap: isStory ? 96 : 64,
            fontFamily: FONT_INTER,
            fontSize: isStory ? 28 : 22,
            fontWeight: 600,
            letterSpacing: "0.32em",
            color: `${INK}99`,
            textTransform: "uppercase",
            marginBottom: isStory ? 28 : 18,
          }}
        >
          <span>{content.home.code.toUpperCase()}</span>
          <span>{content.away.code.toUpperCase()}</span>
        </div>

        {/* The mega score */}
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "baseline",
            gap: isStory ? 24 : 18,
            fontFamily: FONT_INTER,
            fontWeight: 900,
            letterSpacing: "-0.05em",
            color: INK,
          }}
        >
          <span
            style={{
              fontSize: isStory ? 480 : 360,
              lineHeight: 1,
              fontVariantNumeric: "tabular-nums",
            }}
          >
            {content.homeScore}
          </span>
          <span
            style={{
              fontSize: isStory ? 200 : 150,
              color: `${INK}55`,
              fontWeight: 200,
            }}
          >
            –
          </span>
          <span
            style={{
              fontSize: isStory ? 480 : 360,
              lineHeight: 1,
              fontVariantNumeric: "tabular-nums",
            }}
          >
            {content.awayScore}
          </span>
        </div>

        {/* Team names */}
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            gap: isStory ? 96 : 64,
            fontFamily: FONT_INTER,
            fontSize: isStory ? 36 : 28,
            fontWeight: 700,
            letterSpacing: "-0.01em",
            color: INK,
            marginTop: isStory ? 36 : 24,
          }}
        >
          <span>{content.home.name}</span>
          <span>{content.away.name}</span>
        </div>
      </div>

      {content.tagline && (
        <div
          style={{
            fontFamily: FONT_INTER,
            fontSize: isStory ? 22 : 18,
            color: `${INK}aa`,
            textAlign: "center",
            fontStyle: "italic",
            marginBottom: 16,
          }}
        >
          {content.tagline}
        </div>
      )}
      <div
        style={{
          fontFamily: FONT_INTER,
          fontSize: isStory ? 16 : 13,
          fontWeight: 600,
          letterSpacing: "0.25em",
          textTransform: "uppercase",
          color: `${INK}66`,
          textAlign: "center",
        }}
      >
        {[content.dateLabel, content.venue].filter(Boolean).join(" · ")}
      </div>
    </div>
  );
}

// ─── THEME 7 · CINEMA ──────────────────────────────────────────────────────
//   Pitch-black. Gold spotlight cone on the winner. Loser fades to shadow.
//   The goosebump-tier dramatic poster treatment.

function CinemaTheme({
  content,
  winner,
  format,
}: {
  content: MatchResultCardContent;
  winner: "home" | "away" | "draw";
  format: CardFormat;
}) {
  const isStory = format === "story";
  const homeIsWinner = winner === "home";
  const awayIsWinner = winner === "away";

  // Spotlight position — left for home, right for away, top-center for draw
  const spotlightCx = winner === "draw" ? "50%" : homeIsWinner ? "30%" : "70%";
  const spotlightCy = "40%";

  return (
    <>
      {/* Spotlight cone */}
      <div
        style={{
          position: "absolute",
          inset: 0,
          background: `radial-gradient(ellipse 45% 60% at ${spotlightCx} ${spotlightCy}, rgba(255,215,0,0.32) 0%, rgba(255,215,0,0.08) 40%, transparent 70%)`,
          zIndex: 1,
        }}
      />
      <div
        style={{
          position: "relative",
          zIndex: 2,
          flex: 1,
          display: "flex",
          flexDirection: "column",
          padding: isStory ? "60px 40px" : "36px 28px",
        }}
      >
        <div
          style={{
            textAlign: "center",
            fontFamily: FONT_MONO,
            fontSize: isStory ? 22 : 18,
            color: "#FFD700",
            letterSpacing: "0.5em",
            textTransform: "uppercase",
            fontWeight: 800,
            opacity: 0.9,
          }}
        >
          {content.eyebrow}
        </div>

        <div
          style={{
            flex: 1,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: isStory ? 24 : 16,
            padding: isStory ? "32px 0" : "20px 0",
          }}
        >
          <CinemaSide
            team={content.home}
            score={content.homeScore}
            isWinner={homeIsWinner}
            isDraw={winner === "draw"}
            isStory={isStory}
            align="right"
          />
          <div
            style={{
              fontFamily: FONT_BEBAS,
              fontSize: isStory ? 200 : 152,
              color: "rgba(255,215,0,0.45)",
              lineHeight: 1,
              padding: "0 12px",
            }}
          >
            ·
          </div>
          <CinemaSide
            team={content.away}
            score={content.awayScore}
            isWinner={awayIsWinner}
            isDraw={winner === "draw"}
            isStory={isStory}
            align="left"
          />
        </div>

        {content.headline && (
          <div
            style={{
              fontFamily: FONT_PLAYFAIR,
              fontSize: isStory ? 64 : 48,
              fontStyle: "italic",
              fontWeight: 700,
              color: "#FFD700",
              textAlign: "center",
              letterSpacing: "-0.01em",
              lineHeight: 1.1,
              marginBottom: 14,
              maxWidth: isStory ? 880 : 720,
              alignSelf: "center",
              textShadow: "0 6px 24px rgba(255,215,0,0.4)",
            }}
          >
            {content.headline}
          </div>
        )}
        {content.tagline && (
          <div
            style={{
              fontFamily: FONT_INTER,
              fontSize: isStory ? 26 : 22,
              color: "rgba(255,255,255,0.88)",
              fontStyle: "italic",
              textAlign: "center",
              maxWidth: isStory ? 820 : 680,
              alignSelf: "center",
              marginBottom: 14,
            }}
          >
            "{content.tagline}"
          </div>
        )}
        <MetaStrip content={content} isStory={isStory} color="rgba(255,215,0,0.6)" />
      </div>
    </>
  );
}

function CinemaSide({
  team,
  score,
  isWinner,
  isDraw,
  isStory,
  align,
}: {
  team: MatchResultTeam;
  score: number;
  isWinner: boolean;
  isDraw: boolean;
  isStory: boolean;
  align: "left" | "right";
}) {
  const opacity = isWinner || isDraw ? 1 : 0.28;
  const textColor = isWinner ? "#FFD700" : isDraw ? "#FFFFFF" : "rgba(255,255,255,0.35)";
  return (
    <div
      style={{
        flex: 1,
        display: "flex",
        flexDirection: "column",
        alignItems: align === "left" ? "flex-start" : "flex-end",
        gap: isStory ? 18 : 12,
        opacity,
        transition: "opacity 0.3s",
        minWidth: 0,
      }}
    >
      <FlagImg
        team={team}
        width={isStory ? 180 : 140}
        ring={isWinner ? "#FFD700" : "rgba(255,255,255,0.18)"}
      />
      <div
        style={{
          fontFamily: FONT_BEBAS,
          fontSize: isStory ? 62 : 48,
          color: textColor,
          letterSpacing: "0.04em",
          textTransform: "uppercase",
          lineHeight: 1,
          textAlign: align,
        }}
      >
        {team.name}
      </div>
      <div
        style={{
          fontFamily: FONT_BEBAS,
          fontSize: isStory ? 320 : 240,
          color: textColor,
          lineHeight: 0.85,
          letterSpacing: "-0.05em",
          textShadow: isWinner
            ? "0 0 80px rgba(255,215,0,0.6), 0 6px 24px rgba(0,0,0,0.7)"
            : "0 4px 12px rgba(0,0,0,0.6)",
          fontVariantNumeric: "tabular-nums",
        }}
      >
        {score}
      </div>
    </div>
  );
}

// ─── THEME 8 · TICKER ──────────────────────────────────────────────────────
//   Sky Sports / ESPN broadcast lower-third bar. Score in the middle.

function TickerTheme({
  content,
  format,
}: {
  content: MatchResultCardContent;
  format: CardFormat;
}) {
  const isStory = format === "story";
  const homeColor = teamColor(content.home, "#0D8C3C");
  const awayColor = teamColor(content.away, "#0D5C2E");

  return (
    <div
      style={{
        position: "relative",
        zIndex: 2,
        flex: 1,
        display: "flex",
        flexDirection: "column",
      }}
    >
      {/* Top — eyebrow */}
      <div
        style={{
          textAlign: "center",
          fontFamily: FONT_INTER,
          fontSize: isStory ? 24 : 20,
          fontWeight: 800,
          color: "#FFD700",
          letterSpacing: "0.4em",
          textTransform: "uppercase",
          marginTop: isStory ? 80 : 44,
        }}
      >
        {content.eyebrow}
      </div>

      {/* Headline */}
      {content.headline && (
        <div
          style={{
            fontFamily: FONT_BEBAS,
            fontSize: isStory ? 96 : 72,
            color: "#FFFFFF",
            letterSpacing: "0.02em",
            textTransform: "uppercase",
            textAlign: "center",
            margin: isStory ? "32px 32px 0" : "20px 22px 0",
            lineHeight: 0.95,
            textShadow: "0 4px 20px rgba(0,0,0,0.5)",
          }}
        >
          {content.headline}
        </div>
      )}

      {/* The broadcast bar */}
      <div style={{ flex: 1, display: "flex", alignItems: "center", padding: isStory ? "40px 0" : "28px 0" }}>
        <div
          style={{
            width: "100%",
            display: "flex",
            alignItems: "stretch",
            height: isStory ? 280 : 210,
            boxShadow: "0 30px 80px rgba(0,0,0,0.5)",
          }}
        >
          {/* Home block */}
          <div
            style={{
              flex: 1,
              background: homeColor,
              color: luminance(homeColor) > 0.55 ? "#0a0a0a" : "#FFFFFF",
              display: "flex",
              alignItems: "center",
              padding: isStory ? "0 32px" : "0 22px",
              gap: isStory ? 22 : 16,
            }}
          >
            <FlagImg team={content.home} width={isStory ? 110 : 84} ring="rgba(255,255,255,0.4)" />
            <div
              style={{
                flex: 1,
                fontFamily: FONT_BEBAS,
                fontSize: isStory ? 80 : 60,
                letterSpacing: "0.02em",
                textTransform: "uppercase",
                lineHeight: 1,
              }}
            >
              {content.home.name}
            </div>
          </div>
          {/* Score block */}
          <div
            style={{
              flex: "0 0 auto",
              minWidth: isStory ? 360 : 280,
              background: "#0a0a0a",
              color: "#FFFFFF",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: isStory ? 24 : 18,
              borderLeft: "4px solid #FFD700",
              borderRight: "4px solid #FFD700",
            }}
          >
            <span
              style={{
                fontFamily: FONT_BEBAS,
                fontSize: isStory ? 220 : 168,
                lineHeight: 1,
                fontVariantNumeric: "tabular-nums",
              }}
            >
              {content.homeScore}
            </span>
            <span
              style={{
                fontFamily: FONT_BEBAS,
                fontSize: isStory ? 140 : 104,
                color: "#FFD700",
                lineHeight: 1,
              }}
            >
              −
            </span>
            <span
              style={{
                fontFamily: FONT_BEBAS,
                fontSize: isStory ? 220 : 168,
                lineHeight: 1,
                fontVariantNumeric: "tabular-nums",
              }}
            >
              {content.awayScore}
            </span>
          </div>
          {/* Away block */}
          <div
            style={{
              flex: 1,
              background: awayColor,
              color: luminance(awayColor) > 0.55 ? "#0a0a0a" : "#FFFFFF",
              display: "flex",
              alignItems: "center",
              flexDirection: "row-reverse",
              padding: isStory ? "0 32px" : "0 22px",
              gap: isStory ? 22 : 16,
            }}
          >
            <FlagImg team={content.away} width={isStory ? 110 : 84} ring="rgba(255,255,255,0.4)" />
            <div
              style={{
                flex: 1,
                fontFamily: FONT_BEBAS,
                fontSize: isStory ? 80 : 60,
                letterSpacing: "0.02em",
                textTransform: "uppercase",
                lineHeight: 1,
                textAlign: "right",
              }}
            >
              {content.away.name}
            </div>
          </div>
        </div>
      </div>

      {/* Bottom — meta */}
      <div
        style={{
          padding: isStory ? "0 40px 40px" : "0 28px 24px",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          gap: 10,
        }}
      >
        {content.tagline && (
          <div
            style={{
              fontFamily: FONT_INTER,
              fontSize: isStory ? 26 : 22,
              color: "rgba(255,255,255,0.95)",
              fontStyle: "italic",
              textAlign: "center",
              maxWidth: isStory ? 820 : 660,
            }}
          >
            "{content.tagline}"
          </div>
        )}
        {(content.goals?.length ?? 0) > 0 && (
          <GoalsStrip goals={content.goals!} isStory={isStory} textColor="rgba(255,255,255,0.92)" />
        )}
        <MetaStrip content={content} isStory={isStory} color="rgba(255,255,255,0.8)" />
      </div>
    </div>
  );
}

// ─── Shared goal strip (used by STADIUM + TICKER) ────────────────────────────

function GoalsStrip({
  goals,
  isStory,
  textColor,
}: {
  goals: MatchResultGoal[];
  isStory: boolean;
  textColor: string;
}) {
  const home = goals.filter((g) => g.side === "home");
  const away = goals.filter((g) => g.side === "away");
  const fontSize = isStory ? 22 : 18;
  return (
    <div
      style={{
        display: "flex",
        justifyContent: "center",
        gap: isStory ? 48 : 28,
        marginTop: 6,
      }}
    >
      <div style={{ display: "flex", flexDirection: "column", gap: 4, alignItems: "flex-end" }}>
        {home.map((g, i) => (
          <GoalRow key={i} goal={g} color={textColor} fontSize={fontSize} />
        ))}
      </div>
      <div style={{ display: "flex", flexDirection: "column", gap: 4, alignItems: "flex-start" }}>
        {away.map((g, i) => (
          <GoalRow key={i} goal={g} color={textColor} fontSize={fontSize} />
        ))}
      </div>
    </div>
  );
}
